import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetLevels } from '../../hooks/levels/useGetLevels';
import { useDeleteLevel } from '../../hooks/levels/useDeleteLevel';
import { useDeleteAllLevels } from '../../hooks/levels/useDeleteAllLevels';
import { useForceDeleteLevel } from '../../hooks/levels/useForceDeleteLevel';
import { useForceDeleteAllLevels } from '../../hooks/levels/useForceDeleteAllLevels';
import { useRestoreLevel } from '../../hooks/levels/useRestoreLevel';
import { useRestoreAllLevels } from '../../hooks/levels/useRestoreAllLevels';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import LevelsTableColumns from './Table/LevelsTableColumns';
import LevelsTableRow from './Table/LevelsTableRow';

const LevelsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const { currentTermId } = useCurrentContext();
    const [filterTermId, setFilterTermId] = useState('');

    useEffect(() => {
        if (currentTermId && !filterTermId) setFilterTermId(currentTermId);
    }, [currentTermId]);

    const perms = useMemo(() => ({
        list: has('levels_list'),
        create: has('levels_create'),
        edit: has('levels_edit'),
        delete: has('levels_delete'),
        archive_list: has('levels_archive_list'),
        restore: has('levels_restore'),
        force_delete: has('levels_force_delete'),
    }), [has]);

    const { deleteLevel, isDelete } = useDeleteLevel();
    const { deleteAllLevels, isDelete: isDeletingAll } = useDeleteAllLevels();
    const { forceDeleteLevel } = useForceDeleteLevel();
    const { forceDeleteAllLevels } = useForceDeleteAllLevels();
    const { restoreLevel } = useRestoreLevel();
    const { restoreAllLevels } = useRestoreAllLevels();

    const { academicTerms } = useGetAcademicTerms(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/levels',
        deleteSingle: deleteLevel,
        deleteAll: deleteAllLevels,
        forceDeleteSingle: forceDeleteLevel,
        forceDeleteAll: forceDeleteAllLevels,
        restoreSingle: restoreLevel,
        restoreAll: restoreAllLevels,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        academic_term_id: filterTermId || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterTermId, isArchive]);

    const { isLoading, error, levels, meta } = useGetLevels(page, perPage, filters);

    const renderRow = (params) => (
        <LevelsTableRow
            key={params.key || params.item.id}
            item={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={levels.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    value={filterTermId}
                    onChange={(e) => setFilterTermId(e.target.value)}
                    label={isArabic ? 'فلتر حسب الفصل' : 'Filter by Term'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{
                        minWidth: 220,
                        ...(isArabic && {
                            '& .MuiInputLabel-root': {
                                left: 'unset',
                                right: 32,
                                transformOrigin: 'top right',
                            },
                            '& .MuiInputLabel-shrink': {
                                right: 32,
                            },
                            '& .MuiSelect-icon': { left: 7, right: 'unset' },
                            '& .MuiSelect-select': {
                                textAlign: 'right',
                                paddingRight: '14px !important',
                                paddingLeft: '32px !important',
                            },
                            '& .MuiOutlinedInput-notchedOutline legend': {
                                textAlign: 'right',
                            },
                        }),
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: { direction: isArabic ? 'rtl' : 'ltr' },
                            },
                        },
                    }}
                >
                    <MenuItem value="">
                        {isArabic ? 'الكل' : 'All'}
                    </MenuItem>
                    {(academicTerms || []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.name}
                        </MenuItem>
                    ))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && (
                        <ArchiveButton to="/levels/archive" />
                    )}
                    {isArchive && perms.list && (
                        <ArchiveButton
                            to="/levels"
                            archiveLabelAr="العودة للقائمة"
                            archiveLabelEn="Back to List"
                        />
                    )}
                </Box>
            </Box>

            <TableLayout
                title={isArabic
                    ? (isArchive ? 'أرشيف المستويات' : 'المستويات')
                    : (isArchive ? 'Levels Archive' : 'Levels')}
                subtitle={isArabic
                    ? (isArchive ? 'إدارة المستويات المحذوفة' : 'إدارة المستويات في النظام')
                    : (isArchive ? 'Manage deleted levels' : 'Manage levels')}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة مستوى' : 'Add Level'}
                onAddClick={() => navigate('/levels/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading}
                error={error}
                items={levels}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, levels)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={LevelsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive
                    ? (perms.force_delete ? handleMenuDelete : undefined)
                    : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد مستويات في الأرشيف' : 'لا توجد مستويات'}
                emptyLabelEn={isArchive ? 'No archived levels' : 'No levels'}
                loadErrorAr="حدث خطأ أثناء تحميل المستويات"
                loadErrorEn="Failed to load levels"
            />
        </>
    );
};

export default LevelsTable;
